import { useCallback, useRef, useState } from 'react';
import { useSettingsStore } from '../../stores/settingsStore';
import { api } from '../lib/api';

// Кэш: text+lang → AudioBuffer чтобы не перезагружать при повторном воспроизведении
const audioCache = new Map<string, string>();

/**
 * useTTS — синтез речи
 * Первичный: серверный gTTS через /api/ai/tts (натуральное звучание)
 * Fallback: браузерный SpeechSynthesis (если сервер недоступен)
 */
export function useTTS() {
  const { ttsEnabled: tts_enabled } = useSettingsStore();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [speaking, setSpeaking] = useState(false);

  const speak = useCallback(
    async (text: string, lang: string = 'en-US') => {
      if (!tts_enabled) return;

      // Останавливаем предыдущее воспроизведение
      window.speechSynthesis?.cancel();
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }

      const langCode = lang.split('-')[0]; // en-US → en
      const cacheKey = `${langCode}:${text}`;

      // Проверяем кэш
      let blobUrl = audioCache.get(cacheKey);

      if (!blobUrl) {
        // Серверный gTTS (primary) — натуральное звучание
        try {
          const blob = await api.tts(text, langCode);
          blobUrl = URL.createObjectURL(blob);
          audioCache.set(cacheKey, blobUrl);
          // Ограничиваем размер кэша
          if (audioCache.size > 50) {
            const firstKey = audioCache.keys().next().value!;
            URL.revokeObjectURL(audioCache.get(firstKey)!);
            audioCache.delete(firstKey);
          }
        } catch {
          // Fallback: браузерный SpeechSynthesis
          if ('speechSynthesis' in window) {
            const utterance = new SpeechSynthesisUtterance(text);
            utterance.lang = lang;
            utterance.rate = 0.9;
            utterance.pitch = 1.0;

            const voices = window.speechSynthesis.getVoices();
            const preferred = voices.find(
              v => v.lang.startsWith(langCode) && v.localService === true
            ) || voices.find(v => v.lang.startsWith(langCode));
            if (preferred) utterance.voice = preferred;

            utterance.onstart = () => setSpeaking(true);
            utterance.onend = () => setSpeaking(false);
            utterance.onerror = () => setSpeaking(false);
            window.speechSynthesis.speak(utterance);
          }
          return;
        }
      }

      // Воспроизводим аудио (double-play хак для надёжности в Telegram WebView)
      try {
        const audio = new Audio(blobUrl);
        audioRef.current = audio;
        audio.onplay = () => setSpeaking(true);
        audio.onended = () => setSpeaking(false);
        audio.onpause = () => setSpeaking(false);
        audio.onerror = () => setSpeaking(false);

        // Для Telegram WebView: первый play может быть заблокирован политикой автовоспроизведения
        // Запускаем и сразу обрабатываем ошибку
        audio.play().catch(async () => {
          // Повторная попытка через короткую паузу
          await new Promise(r => setTimeout(r, 100));
          audio.play().catch(() => {
            setSpeaking(false);
          });
        });
      } catch {
        setSpeaking(false);
      }
    },
    [tts_enabled]
  );

  const stop = useCallback(() => {
    setSpeaking(false);
    window.speechSynthesis?.cancel();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
  }, []);

  return { speak, stop, speaking };
}
